import { DateTime } from "luxon";
import { DEFAULT_EXCLUDE_FIX_TYPES } from "common/utils/geo";
import { DeviceLocationHistoryRecord } from "protos/rtc/device_location_history";
import { FixType } from "protos/geo/geo";
import { jobExplorer } from "common/state/jobExplorer";
import { LocationHistoryRecord } from "protos/rtc/location_history";
import {
  PivotAlertState,
  SubscriptionDeviceType,
  SubscriptionMessage,
} from "protos/rtc/web_socket";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { useLocatorWebSocket } from "./useLocatorWebsocket";

export interface LiveRtcTractorsResponse {
  serialToData: Record<string, LiveRtcTractorResponse>;
}
export interface LiveRtcTractorResponse extends LiveRtcResponse {
  location: LocationHistoryRecord | undefined;
}

export interface LiveRtcDeviceResponse extends LiveRtcResponse {
  location: DeviceLocationHistoryRecord | undefined;
}

export interface LiveRtcResponse {
  isStale: boolean;
  isMoving: boolean;
  lastCheckedTimestamp: string | null;
}

export const useDeviceLocations = (
  deviceIds: string[],
  onLocationUpdate?: (
    resp: DeviceLocationHistoryRecord & { id: string }
  ) => void,
  excludeFixTypes?: FixType[]
): void => {
  const [lastDeviceIds, setLastDeviceIds] = useState(new Set<string>());
  const dispatch = useDispatch();
  const { authenticated, subscribe, unsubscribe } = useLocatorWebSocket({
    onLocationUpdate: (m: SubscriptionMessage) => {
      const deviceId = m.location?.device?.deviceId;
      const data = m.location?.deviceRec;
      if (deviceId && data) {
        dispatch(jobExplorer.actions.setDeviceData({ deviceId, data }));
        onLocationUpdate?.({
          id: deviceId,
          ...data,
        });
      }
    },
  });

  useEffect(() => {
    if (authenticated) {
      const deviceIdsSet = new Set(deviceIds);
      const missingDeviceIds = lastDeviceIds.difference(deviceIdsSet);
      const newDeviceIds = deviceIdsSet.difference(lastDeviceIds);

      for (const serial of newDeviceIds) {
        subscribe(
          serial,
          SubscriptionDeviceType.DEVICE,
          excludeFixTypes ?? DEFAULT_EXCLUDE_FIX_TYPES
        );
      }

      for (const serial of missingDeviceIds) {
        unsubscribe(serial, SubscriptionDeviceType.DEVICE);
      }

      // break update loop if the device IDs haven't actually changed
      if (newDeviceIds.size > 0 || missingDeviceIds.size > 0) {
        setLastDeviceIds(deviceIdsSet);
      }
    }
  }, [
    authenticated,
    subscribe,
    deviceIds,
    lastDeviceIds,
    unsubscribe,
    excludeFixTypes,
  ]);
};

export const useTractorLocations = (
  serials: string[],
  onLocationUpdate?: (resp: LocationHistoryRecord & { serial: string }) => void
): void => {
  const [lastSerials, setLastSerials] = useState(new Set<string>());
  const dispatch = useDispatch();
  const { authenticated, subscribe, unsubscribe } = useLocatorWebSocket({
    onLocationUpdate: (m: SubscriptionMessage) => {
      const serial = m.location?.device?.serial;
      const data = m.location?.robotRec;
      if (serial && data) {
        dispatch(jobExplorer.actions.setTractorData({ serial, data }));
        onLocationUpdate?.({
          serial,
          ...data,
        });
      }
    },
  });

  useEffect(() => {
    if (authenticated) {
      const serialsSet = new Set(serials);
      const missingSerials = lastSerials.difference(serialsSet);
      const newSerials = serialsSet.difference(lastSerials);

      for (const serial of newSerials) {
        // note (cqb): we don't currently use the default excluded fix types
        // here because we haven't had problems with robots blasting off into
        // space when they lose GPS lock.  the option is there, though.
        subscribe(serial, SubscriptionDeviceType.ROBOT);
      }

      for (const serial of missingSerials) {
        unsubscribe(serial, SubscriptionDeviceType.ROBOT);
      }

      // break update loop if the serials haven't actually changed
      if (newSerials.size > 0 || missingSerials.size > 0) {
        setLastSerials(serialsSet);
      }
    }
  }, [authenticated, subscribe, serials, lastSerials, unsubscribe]);
};

export const usePivotAlerts = (
  serials: string[],
  onLocationUpdate?: (resp: PivotAlertState & { serial: string }) => void
): void => {
  const [lastSerials, setLastSerials] = useState(new Set<string>());
  const { authenticated, subscribe, unsubscribe } = useLocatorWebSocket({
    onLocationUpdate: (m: SubscriptionMessage) => {
      const serial = m.location?.device?.serial;
      const data = m.location?.pivotAlert;
      if (serial && data) {
        onLocationUpdate?.({
          serial,
          ...data,
        });
      }
    },
  });

  useEffect(() => {
    if (authenticated) {
      const serialsSet = new Set(serials);
      const missingSerials = lastSerials.difference(serialsSet);
      const newSerials = serialsSet.difference(lastSerials);

      for (const serial of newSerials) {
        subscribe(serial, SubscriptionDeviceType.PIVOT_ALERT);
      }

      for (const serial of missingSerials) {
        unsubscribe(serial, SubscriptionDeviceType.PIVOT_ALERT);
      }

      // break update loop if the serials haven't actually changed
      if (newSerials.size > 0 || missingSerials.size > 0) {
        setLastSerials(serialsSet);
      }
    }
  }, [authenticated, subscribe, serials, lastSerials, unsubscribe]);
};

export const useStalePercentage = (
  pollIntervalSeconds: number,
  staleThresholdSeconds: number,
  lastCheckedTimestamp: string | null
): number => {
  const [now, setNow] = useState<DateTime>(DateTime.now());
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(DateTime.now());
    }, (pollIntervalSeconds / staleThresholdSeconds) * 1000);
    return () => {
      clearInterval(interval);
    };
  }, [pollIntervalSeconds, staleThresholdSeconds]);
  return lastCheckedTimestamp
    ? (now.toSeconds() - DateTime.fromISO(lastCheckedTimestamp).toSeconds()) /
        staleThresholdSeconds
    : 1;
};
